import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared button primitive used inside cards. Three variants:
 *
 *   primary   — pink/coral gradient, white text. Use for the "default" action.
 *   secondary — white background + grey border, dark text. Use for "Read"/"Watch"/etc.
 *   gold      — warm amber gradient, white text. Use for "Achieved" type accents.
 *   cta       — full-width orange/coral block, py-3 14px. Use for primary card CTA
 *               like "Open solids guide" / "Continue with Google".
 */

export type CardButtonVariant = 'primary' | 'secondary' | 'gold' | 'cta';

interface CardButtonProps {
  variant?: CardButtonVariant;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
  /** When set, button takes the full row width instead of flex-1 within a row. */
  fullWidth?: boolean;
  className?: string;
}

const VARIANT_STYLES: Record<CardButtonVariant, CSSProperties> = {
  primary: {
    background: 'linear-gradient(to right, hsl(18 90% 70%), hsl(340 72% 60%))',
    color: 'white',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
  },
  secondary: {
    background: 'white',
    color: 'hsl(var(--foreground))',
    fontSize: 13,
    fontWeight: 600,
    border: '1.5px solid hsl(var(--border))',
  },
  gold: {
    background: 'linear-gradient(to right, hsl(42 90% 60%), hsl(35 82% 52%))',
    color: 'white',
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
  },
  cta: {
    background: 'hsl(var(--feed-accent))',
    color: 'white',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
  },
};

const VARIANT_PADDING: Record<CardButtonVariant, string> = {
  primary: 'py-2.5',
  secondary: 'py-2.5',
  gold: 'py-2.5',
  cta: 'py-3',
};

export const CardButton = ({
  variant = 'secondary',
  onClick,
  disabled,
  children,
  fullWidth = false,
  className = '',
}: CardButtonProps) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`${fullWidth ? 'w-full' : 'flex-1'} ${VARIANT_PADDING[variant]} rounded-full flex items-center justify-center gap-1.5 active:opacity-90 disabled:opacity-50 transition-opacity ${className}`}
    style={VARIANT_STYLES[variant]}
  >
    {children}
  </button>
);
